/**
 * UploadQueueSection
 * Renders the current upload queue on the /models page, above the model grid.
 * Reads queue state directly from UploadContext — no props required.
 */

import React from 'react';
import { FileIcon, X } from 'lucide-react';
import { useUploadContext, UploadQueueItem, UploadItemStatus } from '../../contexts/UploadContext';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const statusColour: Record<UploadItemStatus, string> = {
  queued:     'text-gray-500',
  uploading:  'text-blue-600',
  processing: 'text-amber-600',
  complete:   'text-emerald-600',
  error:      'text-red-600',
};

function statusLabel(item: UploadQueueItem): string {
  switch (item.status) {
    case 'queued':     return 'Queued';
    case 'uploading':  return `Uploading ${item.progress}%`;
    case 'processing': return 'Processing…';
    case 'complete':   return 'Complete';
    case 'error':      return 'Failed';
  }
}

// ---------------------------------------------------------------------------
// Row
// ---------------------------------------------------------------------------

interface UploadRowProps {
  item: UploadQueueItem;
  onCancel: (id: string) => void;
}

const UploadRow: React.FC<UploadRowProps> = ({ item, onCancel }) => {
  const canCancel = item.status === 'queued' || item.status === 'uploading';
  const canDismiss = item.status === 'complete' || item.status === 'error';
  const showButton = canCancel || canDismiss;

  return (
    <li className="flex flex-col border-b border-gray-100 py-2 last:border-0">
      <div className="flex items-center gap-3">
        {/* File icon */}
        <FileIcon className="h-4 w-4 flex-shrink-0 text-gray-400" aria-hidden="true" />

        {/* Filename */}
        <span className="min-w-0 flex-1 truncate text-sm text-gray-800" title={item.name}>
          {item.name}
        </span>

        {/* Status badge */}
        <span className={`flex-shrink-0 text-xs font-medium ${statusColour[item.status]}`}>
          {statusLabel(item)}
        </span>

        {/* Cancel / dismiss button */}
        {showButton && (
          <button
            type="button"
            onClick={() => onCancel(item.id)}
            title={canCancel ? 'Cancel upload' : 'Dismiss'}
            className="flex-shrink-0 rounded p-0.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-primary-500"
            aria-label={canCancel ? 'Cancel upload' : 'Dismiss'}
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </button>
        )}
      </div>

      {/* Progress bar — only whilst uploading */}
      {item.status === 'uploading' && (
        <div className="mt-1.5 h-1 w-full overflow-hidden rounded-full bg-gray-100" aria-hidden="true">
          <div
            className="h-full rounded-full bg-blue-500 transition-[width] duration-200"
            style={{ width: `${item.progress}%` }}
          />
        </div>
      )}
    </li>
  );
};

// ---------------------------------------------------------------------------
// Section
// ---------------------------------------------------------------------------

const UploadQueueSection: React.FC = () => {
  const { queue, cancelUpload, dismissCompleted } = useUploadContext();

  if (queue.length === 0) return null;

  const hasFinished = queue.some(i => i.status === 'complete' || i.status === 'error');

  return (
    <section aria-label="Upload queue" className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
      <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-gray-500">
        Uploads
      </h2>

      <ul className="divide-y-0">
        {queue.map(item => (
          <UploadRow key={item.id} item={item} onCancel={cancelUpload} />
        ))}
      </ul>

      {/* Clear all — only shown once at least one item has finished */}
      {hasFinished && (
        <div className="mt-3 text-right">
          <button
            type="button"
            onClick={dismissCompleted}
            className="text-xs text-gray-400 hover:text-gray-600 underline focus:outline-none focus:ring-2 focus:ring-primary-500 rounded"
          >
            Clear completed
          </button>
        </div>
      )}
    </section>
  );
};

export default UploadQueueSection;
